"use client"

import { useState, useContext, useCallback } from "react"
import { Plus, Upload, RefreshCw } from "lucide-react"
import { Button } from "../components/ui/Button"
import { useToast } from "../hooks/useToast"

// Context
import { AuthContext } from "../../Auth/context/AuthContext"

// Custom hook
import useCostCodes from "../hooks/useCostCodes"
import useCostCodeCategories from "../hooks/useCostCodeCategories"

// API
import { createCostCode, updateCostCode, toggleCostCodeActive, deleteCostCode } from "../api/costCodes"

// Components
import CountsCards from "../components/CountsCards"
import SearchBar from "../components/SearchBar"
import CostCodesTable from "../components/CostCodesTable"
import CreateEditModal from "../components/CreateEditModal"
import ImportWizardModal from "../components/ImportWizardModal"

const CostCodesPage = () => {
  const { auth } = useContext(AuthContext)
  const { toast } = useToast()

  // Search state
  const [searchTerm, setSearchTerm] = useState("")

  // (Pagination removed; always fetch all codes without paging)

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [selectedCode, setSelectedCode] = useState(null)

  // Fetch cost codes using custom hook
  const { codes, loading, error, refetch } = useCostCodes({
    token: auth?.token,
    noPagination: true,
    search: searchTerm,
  })
  // Fetch distinct categories for combobox and filters
  const {
    categories: costCodeCategories,
    refetch: refetchCategories,
  } = useCostCodeCategories({ token: auth?.token })



  // Search handler
  // Search handler
  const handleSearch = useCallback((value) => {
    setSearchTerm(value)
  }, [])

  // Modal handlers
  const handleOpenCreateModal = () => setCreateModalOpen(true)
  const handleCloseCreateModal = () => setCreateModalOpen(false)

  const handleOpenEditModal = (code) => {
    setSelectedCode(code)
    setEditModalOpen(true)
  }
  const handleCloseEditModal = () => {
    setSelectedCode(null)
    setEditModalOpen(false)
  }

  const handleOpenImportModal = () => setImportModalOpen(true)
  // Category filter state
  const [selectedCategory, setSelectedCategory] = useState(null)
  const handleCloseImportModal = () => setImportModalOpen(false)

  // CRUD operations
  const handleCreateCostCode = async (data) => {
    try {
      await createCostCode(auth.token, data)
      handleCloseCreateModal()
      refetch()
      refetchCategories()
      toast({
        title: "Success",
        description: "Cost code created successfully",
        variant: "success",
      })
    } catch (err) {
      console.error("Error creating cost code:", err)
      toast({
        title: "Error",
        description: err.message || "Failed to create cost code",
        variant: "destructive",
      })
    }
  }

  const handleUpdateCostCode = async (data) => {
    if (!selectedCode) return

    try {
      await updateCostCode(auth.token, selectedCode._id, data)
      handleCloseEditModal()
      refetch()
      refetchCategories()
      toast({
        title: "Success",
        description: "Cost code updated successfully",
        variant: "success",
      })
    } catch (err) {
      console.error("Error updating cost code:", err)
      toast({
        title: "Error",
        description: err.message || "Failed to update cost code",
        variant: "destructive",
      })
    }
  }
  
  /**
   * Delete or deactivate a cost code.
   * If the code is unused, it will be deleted. Otherwise, it will be deactivated (set active=false).
   * @param {Object} code - Cost code object
   */
  const handleDeleteCostCode = async (code) => {
    try {
      if (!code.isUsed) {
        await deleteCostCode(auth.token, code._id)
        toast({
          title: "Success",
          description: "Cost code deleted successfully",
          variant: "success",
        })
      } else {
        await toggleCostCodeActive(auth.token, code._id, false)
        toast({
          title: "Success",
          description: "Cost code deactivated successfully",
          variant: "success",
        })
      }
      refetch()
    } catch (err) {
      console.error("Error handling cost code deletion/deactivation:", err)
      toast({
        title: "Error",
        description: err.message || "Failed to delete or deactivate cost code",
        variant: "destructive",
      })
    }
  }

  const handleToggleActive = async (code) => {
    try {
      // Optimistic UI update could go here if desired

      // Make API call
      await toggleCostCodeActive(auth.token, code._id, !code.active)

      // Refresh data to ensure consistency
      refetch()

      toast({
        title: "Success",
        description: `Cost code ${code.active ? "deactivated" : "activated"} successfully`,
        variant: "success",
      })
    } catch (err) {
      console.error("Error toggling cost code:", err)

      // Revert optimistic update
      refetch()

      toast({
        title: "Error",
        description: err.message || "Failed to update cost code status",
        variant: "destructive",
      })
    }
  }

  // Handle import completion
  const handleImportComplete = () => {
    refetch()
    toast({
      title: "Import Complete",
      description: "Cost codes have been imported successfully",
      variant: "success",
    })
  }

  if (!auth?.token) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900">
        <div className="text-center">
          <h2 className="text-xl text-white mb-2">Authentication Required</h2>
          <p className="text-gray-400">Please log in to access cost codes.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <h1 className="text-2xl font-bold text-white">Cost Codes</h1>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={refetch}
            className="bg-gray-800 border-gray-700 hover:bg-gray-700 text-white"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>

          <Button onClick={handleOpenCreateModal} className="bg-blue-600 hover:bg-blue-700 text-white">
            <Plus className="h-4 w-4 mr-2" />
            New Cost Code
          </Button>

          <Button onClick={handleOpenImportModal} className="bg-green-600 hover:bg-green-700 text-white">
            <Upload className="h-4 w-4 mr-2" />
            Import Excel
          </Button>
        </div>
      </div>

      <CountsCards
        codes={codes}
        loading={loading}
        onSelectCategory={(category) => {
          setSelectedCategory(category)
        }}
      />
      {selectedCategory && (
        <div className="mb-6 flex items-center space-x-2">
          <span className="text-gray-300">Filter:</span>
          <span className="px-2 py-1 bg-blue-600 text-white rounded-full">{selectedCategory}</span>
          <Button
            variant="outline"
            size="sm"
            className="bg-gray-800 border-gray-700 hover:bg-gray-700 text-white"
            onClick={() => setSelectedCategory(null)}
          >
            Clear Filter
          </Button>
        </div>
      )}

      <div className="mb-6">
        <SearchBar value={searchTerm} onChange={handleSearch} />
      </div>

      {error && <div className="bg-red-900 bg-opacity-30 text-red-400 p-4 rounded-lg mb-6">{error}</div>}

      <CostCodesTable
        codes={selectedCategory ? codes.filter(c => c.category === selectedCategory) : codes}
        onEdit={handleOpenEditModal}
        onToggle={handleToggleActive}
        // Show delete button for organization-level admins ('Organization' or 'admin')
        onDelete={['Organization', 'admin'].includes(auth.user_type)
          ? handleDeleteCostCode
          : undefined}
        loading={loading}
      />

      {/* Modals */}
      <CreateEditModal
        open={createModalOpen}
        onClose={handleCloseCreateModal}
        onSave={handleCreateCostCode}
        mode="create"
        // Pass live categories and codes for validation
        categories={costCodeCategories}
        existingCodes={new Set(codes.map(c => c.code))}
      />

      <CreateEditModal
        open={editModalOpen}
        onClose={handleCloseEditModal}
        onSave={handleUpdateCostCode}
        mode="edit"
        data={selectedCode}
        categories={costCodeCategories}
        existingCodes={new Set(codes.map(c => c.code))}
      />

      <ImportWizardModal
        open={importModalOpen}
        onClose={handleCloseImportModal}
        token={auth.token}
        onComplete={handleImportComplete}
      />
    </div>
  )
}

export default CostCodesPage
